import { useMemo } from 'react'

import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { HulkPaginatedEnvelope } from 'api/hulkApi'
import { DATA_SOURCES, PAGE_DASHBOARD_CONFIG } from 'configuration/widgets'
import { DATES, RANGE_LAG } from 'const/filters'
import { TABLE, TEXT, TIME_SERIES } from 'const/widgets'
import { useOrgContext } from 'context/OrgContext'
import { useUserOverrideContext } from 'context/UserOverrideContext'
import {
    buildMetricCapabilities,
    widgetNeedsMetricCapabilities,
} from 'features/Dashboards/api/metricCapabilities'
import {
    useGetDataSources,
    useGetDataSourceMetricsList,
} from 'features/MetricsGlossary/queries'
import { getPriorPeriodDateRange } from 'helpers/dateRange'
import {
    formatCurrency,
    formatPeriodDeltaDateRange,
    formatSorter,
    formatStringMetrics,
} from 'helpers/params'
import {
    formatAutoFilters,
    formatGoals,
    formatWidgetGroupBy,
    getWidgetApiFunc,
    getWidgetGroupBys,
    getWidgetMetrics,
    MetricCapabilities,
} from 'helpers/ui/dashboardPage'
import { isWidgetHourlyAgg, zoomOut } from 'helpers/widgets'
import { useCerebroApiRequest } from 'hooks'
import { PageId } from 'routing/pages'
import {
    CurrencyCode,
    DateRangeFilter,
    ModifiedWidget,
    Widget,
    WidgetFilterMap,
} from 'types'

interface WidgetDataApi {
    getWidgetData: (
        pageName: PageId,
        widget: Widget,
        filters: WidgetFilterMap,
        currencyCode: CurrencyCode,
        noCount: boolean,
        pageNumber?: number,
        pageSize?: number,
        capabilities?: MetricCapabilities
    ) => Promise<HulkPaginatedEnvelope<any> | null>
    getWidgetPriorPeriodData: (
        pageName: PageId,
        widget: Widget,
        filters: WidgetFilterMap,
        currencyCode: CurrencyCode,
        noCount: boolean,
        pageNumber?: number,
        pageSize?: number,
        capabilities?: MetricCapabilities,
        reportedComparison?: ReportedComparisonWindow
    ) => Promise<HulkPaginatedEnvelope<any> | null>
}

/**
 * True when the server, not the client, decides this widget's comparison window.
 *
 * A data source that stores whole grain units has the server align the window and
 * report what it compared against; recomputing it here would produce a second,
 * differently-defined window. Everything else keeps its client-computed prior
 * period, which is why this is checked inside the query rather than at the call
 * site.
 */
export function usesReportedComparison(widget: Widget): boolean {
    return !!DATA_SOURCES[widget.data_source]?.dateGrain
}

/**
 * The comparison window the server used for this widget's change metrics, read
 * off the main query's response.
 *
 * The plotted prior series and the change-metric denominator have to cover the
 * same weeks, and they cannot be kept in agreement by computing the window twice
 * — hulk's rule ("N grain units immediately preceding") and Forge's ("the same
 * preset one period back") are different definitions, so rounding both pushes
 * them further apart rather than converging them. So there is one authority:
 * whatever hulk reports it compared against is what gets plotted.
 */
export interface ReportedComparisonWindow {
    start: string
    end: string
}

// Resolve the metric capabilities for `widget.data_source` from the glossary.
// `isReady` is true once we either have capabilities or determine the data
// source isn't in the glossary (in which case capabilities stays undefined
// and getWidgetMetrics falls back to its pre-glossary behavior).
function useMetricCapabilities(widget: Widget): {
    capabilities: MetricCapabilities | undefined
    isReady: boolean
} {
    const dataSourceConfig = DATA_SOURCES[widget.data_source]
    const { data: dataSourcesResponse, isLoading: isLoadingDataSources } =
        useGetDataSources({ includeEndOfLife: true })

    const slug = useMemo(() => {
        if (isLoadingDataSources) {
            return undefined
        }
        const metadataKey = dataSourceConfig?.getMetadataKey(widget)
        return (dataSourcesResponse?.data.results ?? []).find(
            (d) => d.metadata_key === metadataKey
        )?.slug
    }, [dataSourcesResponse, isLoadingDataSources, dataSourceConfig, widget])

    const { data: metricsResponse, isLoading: isLoadingMetrics } =
        useGetDataSourceMetricsList(slug)

    const capabilities = useMemo<MetricCapabilities | undefined>(() => {
        const list = metricsResponse?.data.results
        if (!list) {
            return undefined
        }
        return buildMetricCapabilities(list)
    }, [metricsResponse])

    // Ready once data sources resolve and either: slug isn't in the glossary
    // (no capabilities possible), or the metrics list finished loading.
    const isReady = !isLoadingDataSources && (!slug || !isLoadingMetrics)

    return { capabilities, isReady }
}

function buildWidgetFilterParams(
    pageName: PageId,
    widget: Widget,
    filters: WidgetFilterMap,
    datePrefix: string,
    hasHourlyData: boolean,
    fiscalYearStartMonth?: number
): any {
    const { data_source } = widget
    const formatFiltersFunc =
        DATA_SOURCES[data_source]?.formatFiltersFunc ?? (() => ({}))
    const dashboardConfig = PAGE_DASHBOARD_CONFIG[pageName]
    const formatPageFiltersFunc = dashboardConfig?.formatPageFiltersFunc

    const additionalApiParams =
        DATA_SOURCES[data_source]?.getApiParams?.(widget)

    return {
        ...(formatPageFiltersFunc
            ? // temp hack, but this lookup typing is a bit messy
              (formatPageFiltersFunc as any)(
                  filters,
                  datePrefix,
                  hasHourlyData,
                  filters[RANGE_LAG],
                  fiscalYearStartMonth
              )
            : {}),
        ...formatFiltersFunc(filters, hasHourlyData, fiscalYearStartMonth),
        ...additionalApiParams,
    }
}

function buildWidgetParams(
    widget: Widget,
    filterParams: any,
    filters: WidgetFilterMap,
    currencyCode: CurrencyCode,
    datePrefix: string,
    pageNumber?: number,
    pageSize?: number,
    capabilities?: MetricCapabilities,
    fiscalYearStartMonth?: number
): any {
    const dashboardCurrency: CurrencyCode = currencyCode
    const metrics = getWidgetMetrics(widget, capabilities)
    const groupBys = getWidgetGroupBys(widget)

    const pagination =
        pageNumber && pageSize
            ? {
                  limit: pageSize,
                  offset: (pageNumber - 1) * pageSize,
              }
            : {}

    // Time series widgets skip these params because their prior series comes from
    // a second request. The exception is a custom comparison on a source that
    // reports its own window: those dates have no other route to the query
    // builder, which turns them into the comparison window it sends. Every other
    // data source's request keeps the shape it has today — the endpoints that
    // would receive a newly-added param here are not this branch's to change.
    const periodDeltaDateRange =
        widget.type !== TIME_SERIES ||
        (usesReportedComparison(widget) &&
            widget.period_delta_type === 'custom')
            ? widget.period_delta_date_range
            : undefined

    return {
        ...formatStringMetrics(metrics),
        ...filterParams,
        ...pagination,
        ...formatSorter(widget.sorter),
        ...formatCurrency(dashboardCurrency),
        ...formatWidgetGroupBy(
            { ...widget, group_by: groupBys } as unknown as ModifiedWidget,
            filters
        ),
        ...formatAutoFilters(widget),
        ...(periodDeltaDateRange
            ? formatPeriodDeltaDateRange(
                  widget.include_change_metrics ?? false,
                  widget.period_delta_type,
                  periodDeltaDateRange,
                  filters[DATES] as DateRangeFilter,
                  datePrefix,
                  filters[RANGE_LAG] as unknown as number,
                  fiscalYearStartMonth
              )
            : {}),
        ...formatGoals(widget),
    }
}

function useWidgetDataApi(): WidgetDataApi {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { userOverride } = useUserOverrideContext()
    const { org } = useOrgContext()
    const fiscalYearStartMonth = org?.fiscal_year_start_month

    return useMemo(
        () => ({
            getWidgetData: (
                pageName: PageId,
                widget: Widget,
                filters: WidgetFilterMap,
                currencyCode: CurrencyCode,
                noCount: boolean,
                pageNumber?: number,
                pageSize?: number,
                capabilities?: MetricCapabilities
            ) => {
                if (widget.type === TEXT) {
                    return Promise.resolve(null)
                }
                const datePrefix =
                    DATA_SOURCES[widget.data_source]?.datePrefix ?? ''
                const hasHourlyData = isWidgetHourlyAgg(widget)
                const filterParams = buildWidgetFilterParams(
                    pageName,
                    widget,
                    filters,
                    datePrefix,
                    hasHourlyData,
                    fiscalYearStartMonth
                )

                const options = {
                    headers: {
                        noCount: widget.type !== TABLE || noCount,
                        userId: userOverride?.userId,
                        orgId: userOverride?.organizationId,
                        orgGroupId: userOverride?.organizationGroupId,
                    },
                }

                const { widget: tempWidget } = zoomOut(widget, [
                    filterParams[`${datePrefix}_min`],
                    filterParams[`${datePrefix}_max`],
                ])

                const params = buildWidgetParams(
                    tempWidget,
                    filterParams,
                    filters,
                    currencyCode,
                    datePrefix,
                    pageNumber,
                    pageSize,
                    capabilities,
                    fiscalYearStartMonth
                )

                const apiFunc = getWidgetApiFunc(widget)
                return makeCerebroApiRequest({
                    request: apiFunc(params, options),
                    suppressErrorAlerts: true,
                })
            },
            getWidgetPriorPeriodData: (
                pageName: PageId,
                widget: Widget,
                filters: WidgetFilterMap,
                currencyCode: CurrencyCode,
                noCount: boolean,
                pageNumber?: number,
                pageSize?: number,
                capabilities?: MetricCapabilities,
                reportedComparison?: ReportedComparisonWindow
            ) => {
                if (widget.type === TEXT) {
                    return Promise.resolve(null)
                }
                const readsReportedWindow = usesReportedComparison(widget)
                if (readsReportedWindow && !reportedComparison) {
                    return Promise.resolve(null)
                }
                const comparisonWindow = readsReportedWindow
                    ? reportedComparison
                    : undefined
                const datePrefix =
                    DATA_SOURCES[widget.data_source]?.datePrefix ?? ''
                let hasHourlyData = isWidgetHourlyAgg(widget)
                const filterParams = buildWidgetFilterParams(
                    pageName,
                    widget,
                    filters,
                    datePrefix,
                    hasHourlyData,
                    fiscalYearStartMonth
                )

                const options = {
                    headers: {
                        noCount: widget.type !== TABLE || noCount,
                        userId: userOverride?.userId,
                        orgId: userOverride?.organizationId,
                        orgGroupId: userOverride?.organizationGroupId,
                    },
                }

                const { widget: tempWidget, isZoomedOut } = zoomOut(widget, [
                    filterParams[`${datePrefix}_min`],
                    filterParams[`${datePrefix}_max`],
                ])

                if (isZoomedOut) {
                    hasHourlyData = false
                }

                const params = buildWidgetParams(
                    tempWidget,
                    filterParams,
                    filters,
                    currencyCode,
                    datePrefix,
                    pageNumber,
                    pageSize,
                    capabilities,
                    fiscalYearStartMonth
                )
                const dateRange = comparisonWindow
                    ? [comparisonWindow.start, comparisonWindow.end]
                    : getPriorPeriodDateRange(
                          params[`${datePrefix}_min`],
                          params[`${datePrefix}_max`],
                          tempWidget.period_delta_type,
                          filters[DATES] as DateRangeFilter,
                          hasHourlyData,
                          fiscalYearStartMonth
                      )

                // This request exists to fetch one window's numbers. Carrying the
                // widget's comparison settings into it would have the server
                // resolve and join a window before the comparison window, whose
                // columns nothing reads — and with a custom comparison, compare the
                // window against itself. Only stripped where the window came from
                // the server; every other data source sends what it sends today.
                const apiFunc = getWidgetApiFunc(
                    comparisonWindow
                        ? { ...widget, include_change_metrics: false }
                        : widget
                )
                return makeCerebroApiRequest({
                    request: apiFunc(
                        {
                            ...params,
                            [`${datePrefix}_min`]: dateRange[0],
                            [`${datePrefix}_max`]: dateRange[1],
                        },
                        options
                    ),
                    suppressErrorAlerts: true,
                })
            },
        }),
        [
            makeCerebroApiRequest,
            userOverride?.organizationGroupId,
            userOverride?.organizationId,
            userOverride?.userId,
            fiscalYearStartMonth,
        ]
    )
}

export function useGetWidgetDataQuery(
    pageName: PageId,
    widget: Widget,
    filters: WidgetFilterMap,
    currencyCode: CurrencyCode,
    noCount: boolean,
    pageNumber?: number,
    pageSize?: number,
    options?: Omit<
        UseQueryOptions<HulkPaginatedEnvelope<any> | null, AxiosError>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<HulkPaginatedEnvelope<any> | null, AxiosError> {
    const { getWidgetData } = useWidgetDataApi()
    const { orgId, orgGroupId } = useOrgContext()
    const { capabilities, isReady: capabilitiesReady } =
        useMetricCapabilities(widget)

    // Pull out props that shouldn't trigger a reload
    const {
        pagination,
        layout,
        json,
        type,
        name,
        description,
        chart_type,
        show_filter_row,
        stacked,
        stacked_type,
        id, // Doesn't affect the data
        color_palette, // Doesn't affect the data
        constant_value_for_prior_period: _constant_value_for_prior_period, // Doesn't affect the data
        reference_line,
        filters: _filters, // We handle filters separately
        hidden_series,
        ...keyWorthyProps
    } = widget

    const key = {
        ...keyWorthyProps,
        orgId,
        orgGroupId,
        group_by: widget.group_by.map((groupBy) =>
            groupBy && typeof groupBy === 'object' ? groupBy.id : groupBy
        ),
        metrics: widget.metrics.map((metric) =>
            metric && typeof metric === 'object' ? metric.id : metric
        ),
        metrics_by_axis: {
            left: widget.metrics_by_axis?.left?.map((metric) =>
                metric.metrics.map((m) => m.metric)
            ),
            right: widget.metrics_by_axis?.right?.map((metric) =>
                metric.metrics.map((m) => m.metric)
            ),
        },
    }

    // Hold the fetch until the glossary resolves for widgets that would add
    // __total* variants — sending those for metrics that don't support totals
    // makes hulk error rather than silently drop them.
    const callerEnabled = options?.enabled ?? true
    const enabled =
        callerEnabled &&
        (!widgetNeedsMetricCapabilities(widget) || capabilitiesReady)

    return useQuery({
        queryKey: [
            'widget',
            pageName,
            currencyCode,
            noCount,
            key,
            filters,
            pageNumber,
            pageSize,
        ],
        ...options,
        enabled,
        queryFn: () =>
            getWidgetData(
                pageName,
                widget,
                filters,
                currencyCode,
                noCount,
                pageNumber,
                pageSize,
                capabilities
            ),
    })
}

export function useGetWidgetPriorPeriodDataQuery(
    pageName: PageId,
    widget: Widget,
    filters: WidgetFilterMap,
    currencyCode: CurrencyCode,
    noCount: boolean,
    pageNumber?: number,
    pageSize?: number,
    options?: Omit<
        UseQueryOptions<HulkPaginatedEnvelope<any> | null, AxiosError>,
        'queryKey' | 'queryFn'
    >,
    reportedComparison?: ReportedComparisonWindow
): UseQueryResult<HulkPaginatedEnvelope<any> | null, AxiosError> {
    const { getWidgetPriorPeriodData } = useWidgetDataApi()
    const { orgId } = useOrgContext()
    const { capabilities, isReady: capabilitiesReady } =
        useMetricCapabilities(widget)

    // pull out props that shouldn't trigger a reload
    const {
        pagination,
        layout,
        json,
        type,
        name,
        description,
        chart_type,
        show_filter_row,
        stacked,
        stacked_type,
        id, // not including widget id since it doesn't affect the data
        filters: _filters, // we're handling filters separately
        constant_value_for_prior_period: _constant_value_for_prior_period, // not including it since it doesn't affect the data
        reference_line,
        hidden_series,
        ...keyWorthyProps
    } = widget

    const key = {
        ...keyWorthyProps,
        orgId,
        group_by: widget.group_by.map((groupBy) =>
            groupBy && typeof groupBy === 'object' ? groupBy.id : groupBy
        ),
        metrics: widget.metrics.map((metric) =>
            metric && typeof metric === 'object' ? metric.id : metric
        ),
        metrics_by_axis: {
            left: widget.metrics_by_axis?.left?.map((metric) =>
                metric.metrics.map((m) => m.metric)
            ),
            right: widget.metrics_by_axis?.right?.map((metric) =>
                metric.metrics.map((m) => m.metric)
            ),
        },
    }

    // Nothing to plot until the main query reports the window it compared
    // against, so hold the fetch rather than firing it at a second, client-
    // computed window.
    const callerEnabled = options?.enabled ?? true
    const enabled =
        callerEnabled &&
        (!widgetNeedsMetricCapabilities(widget) || capabilitiesReady) &&
        (!usesReportedComparison(widget) || !!reportedComparison)

    return useQuery({
        queryKey: [
            'widgetPriorPeriod',
            pageName,
            currencyCode,
            noCount,
            key,
            filters,
            pageNumber,
            pageSize,
            reportedComparison,
        ],
        ...options,
        enabled,
        queryFn: () =>
            getWidgetPriorPeriodData(
                pageName,
                widget,
                filters,
                currencyCode,
                noCount,
                pageNumber,
                pageSize,
                capabilities,
                reportedComparison
            ),
    })
}
